import { useContext, useEffect, useState } from 'react';

import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';

import {
  MANIFEST_POLL_MS,
  MANIFEST_RETRY_MS,
  WALLBOARD_MANIFEST_PATH,
} from './constants';
import pickManifest from './pickManifest';

export type ManifestState =
  | { status: 'loading' }
  // No screenshots yet: the first render has been requested.
  | { status: 'preparing' }
  // The wallboard was deleted, or this TV's session was ended.
  | { status: 'removed' }
  // Never reached the endpoint.
  | { status: 'unavailable' }
  | { status: 'ready'; manifest: WallboardScreenshots.ManifestResponse };

/**
 * Polls GET /v1/wallboards/manifest. Once a manifest has loaded, a failed poll
 * keeps playing it (screenshots stay viewable until their signed URLs expire).
 */
const useWallboardManifest = (): ManifestState => {
  const { api } = useContext(CloudFunctionClientContext);
  const [state, setState] = useState<ManifestState>({ status: 'loading' });

  useEffect(() => {
    let isActive = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      const response = await api.get<WallboardScreenshots.ManifestResponse>(
        WALLBOARD_MANIFEST_PATH,
      );
      if (!isActive) {
        return;
      }

      let nextPollMs = MANIFEST_POLL_MS;
      if (response.status === 200 && response.data) {
        const next = response.data;
        setState((current) => ({
          status: 'ready',
          manifest: pickManifest({
            current: current.status === 'ready' ? current.manifest : undefined,
            next,
            now: Date.now(),
          }),
        }));
      } else if (response.status === 202) {
        nextPollMs = MANIFEST_RETRY_MS;
        setState((current) =>
          current.status === 'ready' ? current : { status: 'preparing' },
        );
      } else if (response.status === 401 || response.status === 404) {
        setState({ status: 'removed' });
      } else {
        nextPollMs = MANIFEST_RETRY_MS;
        setState((current) =>
          current.status === 'ready' ? current : { status: 'unavailable' },
        );
      }

      timeout = setTimeout(poll, nextPollMs);
    };

    poll();

    return () => {
      isActive = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api]);

  return state;
};

export default useWallboardManifest;
